export const bidConditionFieldOptions = [
  { value: 'annual_income', label: 'Annual income' },
  { value: 'debt_amount', label: 'Debt amount (self-reported)' },
  { value: 'true_debt_amount', label: 'True debt amount' },
  { value: 'credit_score_range', label: 'Credit score' },
  {
    value: 'self_reported_credit_rating',
    label: 'Credit rating (self-reported)',
  },
  { value: 'employment_status', label: 'Employment status' },
  { value: 'pay_frequency', label: 'Pay frequency' },
  { value: 'housing_type', label: 'Housing type' },
  { value: 'state', label: 'State' },
  { value: 'zip', label: 'ZIP' },
  { value: 'age', label: 'Age' },
  { value: 'sub1', label: 'sub1' },
  { value: 'sub3', label: 'sub3' },
  { value: 'days_since_last_seen', label: 'Days since last seen' },
  { value: 'days_since_dnc', label: 'Days since DNC' },
  { value: 'days_since_dnq', label: 'Days since DNQ' },
] as const

export type BidConditionField =
  (typeof bidConditionFieldOptions)[number]['value']

export const bidConditionComparatorOptions = [
  { value: '==', label: 'Equals' },
  { value: '!=', label: 'Not equals' },
  { value: '>', label: '>' },
  { value: '>=', label: '>=' },
  { value: '<', label: '<' },
  { value: '<=', label: '<=' },
] as const

export type BidConditionComparator =
  (typeof bidConditionComparatorOptions)[number]['value']

type BidConditionFieldType = 'numeric' | 'state' | 'zip' | 'text'

const bidConditionFieldTypes: Record<BidConditionField, BidConditionFieldType> =
  {
    annual_income: 'numeric',
    debt_amount: 'numeric',
    true_debt_amount: 'numeric',
    age: 'numeric',
    days_since_last_seen: 'numeric',
    days_since_dnc: 'numeric',
    days_since_dnq: 'numeric',
    state: 'state',
    zip: 'zip',
    credit_score_range: 'text',
    self_reported_credit_rating: 'text',
    employment_status: 'text',
    pay_frequency: 'text',
    housing_type: 'text',
    sub1: 'text',
    sub3: 'text',
  }

export function getComparatorsForField(
  field: BidConditionField,
): readonly (typeof bidConditionComparatorOptions)[number][] {
  if (bidConditionFieldTypes[field] === 'numeric') {
    return bidConditionComparatorOptions
  }
  return bidConditionComparatorOptions.filter(
    (o) => o.value === '==' || o.value === '!=',
  )
}

export function validateConditionValue(
  field: BidConditionField,
  value: string,
): string | null {
  if (value === '') return null
  switch (bidConditionFieldTypes[field]) {
    case 'numeric':
      return Number.isFinite(Number(value)) ? null : 'Must be a number'
    case 'state':
      return /^[A-Za-z]{2}$/.test(value) ? null : 'Use a 2-letter state code'
    case 'zip':
      return /^\d{5}$/.test(value) ? null : 'Use a 5-digit ZIP'
    case 'text':
      return null
  }
}

export interface BidCondition {
  id: number
  rung_id?: number
  field: BidConditionField
  operator: BidConditionComparator
  value: string
  created_at?: string
}

export interface BidMetrics {
  cpa: string | null
  cvr: string | null
  ltc: string | null
  lead_count: number
}

export interface Bid {
  id: number
  partner_id?: number
  name: string
  bid_amount: number
  priority: number
  active: boolean
  conditions: BidCondition[]
  created_at?: string
  metrics?: BidMetrics
}

export type BidOutcomeStatus = 'accepted' | 'rejected'

export interface BidOutcome {
  id: number
  bid_id: number | null
  bid_name: string | null
  status: BidOutcomeStatus
  reason: string
  amount_cents: number | null
  lead_unique_id: string
  created_at: string
}

export const formatCents = (cents: number | null): string =>
  cents == null
    ? '—'
    : `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`

// Mock outcomes
export const mockBidOutcomesByPartnerId: Record<number, BidOutcome[]> = {
  1: [
    {
      id: 1001,
      bid_id: 101,
      bid_name: 'High-income / Good credit',
      status: 'accepted',
      reason: 'matched',
      amount_cents: 3500,
      lead_unique_id: '03baa253c90e4b9b936f95930920a1dc',
      created_at: '2026-04-06T15:12:00Z',
    },
    {
      id: 1002,
      bid_id: 102,
      bid_name: 'Mid-tier debt',
      status: 'accepted',
      reason: 'matched',
      amount_cents: 2000,
      lead_unique_id: 'a7fd91c2b0e44d9aaf84017b3c1e8e22',
      created_at: '2026-04-06T15:08:00Z',
    },
    {
      id: 1003,
      bid_id: null,
      bid_name: null,
      status: 'rejected',
      reason: 'no bid matched (debt_amount < 5000)',
      amount_cents: null,
      lead_unique_id: 'b29014c5e6f64bd1bba0aa8d3c4e2b11',
      created_at: '2026-04-06T15:04:00Z',
    },
    {
      id: 1004,
      bid_id: 103,
      bid_name: 'Fallback',
      status: 'rejected',
      reason: 'rejected: budget exhausted',
      amount_cents: 800,
      lead_unique_id: 'cd8be1f2a05f47c0bd9d28e3f1c5b974',
      created_at: '2026-04-06T14:58:00Z',
    },
    {
      id: 1005,
      bid_id: 101,
      bid_name: 'High-income / Good credit',
      status: 'accepted',
      reason: 'matched',
      amount_cents: 3500,
      lead_unique_id: 'e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9',
      created_at: '2026-04-06T14:42:00Z',
    },
  ],
  12: [
    {
      id: 1201,
      bid_id: 1,
      bid_name: 'test',
      status: 'accepted',
      reason: 'matched',
      amount_cents: 100,
      lead_unique_id: '0e9f2b17a9c3412d8e5a2b19f1e4d8c7',
      created_at: '2026-04-21T16:45:22Z',
    },
    {
      id: 1202,
      bid_id: 1,
      bid_name: 'test',
      status: 'accepted',
      reason: 'matched',
      amount_cents: 100,
      lead_unique_id: 'a1b2c3d4e5f60718293a4b5c6d7e8f90',
      created_at: '2026-04-21T16:32:10Z',
    },
    {
      id: 1203,
      bid_id: null,
      bid_name: null,
      status: 'rejected',
      reason: 'no bid matched (annual_income < 1)',
      amount_cents: null,
      lead_unique_id: 'f90e8d7c6b5a49382716253849ab00cd',
      created_at: '2026-04-21T16:20:58Z',
    },
    {
      id: 1204,
      bid_id: 1,
      bid_name: 'test',
      status: 'rejected',
      reason: 'rejected: duplicate lead',
      amount_cents: 100,
      lead_unique_id: '112233445566778899aabbccddeeff00',
      created_at: '2026-04-21T16:14:30Z',
    },
    {
      id: 1205,
      bid_id: 1,
      bid_name: 'test',
      status: 'accepted',
      reason: 'matched',
      amount_cents: 100,
      lead_unique_id: 'deadbeefcafebabe0123456789abcdef',
      created_at: '2026-04-21T15:58:12Z',
    },
  ],
}

let nextLocalId = -1
export function nextBiddingId(): number {
  return nextLocalId--
}
